import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import { Button, SmileIcon, useUserInfoContext } from '@spotahome/ui-library';
import {
  clientErrorTracker,
  trans,
  useSoyuzExperiment,
  getPagePath
} from '@spotahome/soyuz/client';

import { Mediator } from '@spotahome/soyuz-mediator';
import SoyuzAnalytics from '@spotahome/soyuz-analytics';
import { EXTERNAL_REDIRECTION_LOGIN_MODAL } from '@spotahome/marketplace-common/src/channels';

import { createChatChannel } from '@spotahome/marketplace-common/src/graphql/mutations/chat';

import KeysIcon from '@spotahome/ui-library/src/tenant/components/icons/KeysIcon';

import getGqlClient from '@spotahome/marketplace-common/src/graphql/client';

import './ListingDoubts.scss';
import { ACTION_CREATE_CHAT, ACTION_OPEN_DOUBTS_MODAL } from '../../constants';

import ListingDoubtsModal from './ListingDoubtsModal';

const LANDLORD_OUT_OF_MESSAGE_EXPERIMENT = [];

const ListingDoubts = ({ cityId, listingId, landlordId, countryCode }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { isUserLogged } = useUserInfoContext();

  const chatExperiment = useSoyuzExperiment('Chat_Landlord_2');

  const openListingDoubtsModal = () => {
    setIsModalOpen(true);
    SoyuzAnalytics.sendGA4Event('open', { section: 'listing-doubts-modal' });
  };

  const openMessagesPage = channelId => {
    const url = getPagePath('tenant-messages', { query: { id: channelId } });

    window.open(url, '_self', 'noopener,noreferrer');
  };

  const handleModalClose = () => setIsModalOpen(false);

  const openNewMessageChannel = async () => {
    SoyuzAnalytics.sendGA4Event('open', {
      section: 'listing-landlord-chat',
      context: landlordId
    });

    try {
      const response = await createChatChannel(getGqlClient())({
        listingId
      });

      const channelId = response?.data?.chatCreateChannel?.channelId;

      if (channelId) {
        openMessagesPage(channelId);
      }
    } catch (error) {
      clientErrorTracker.error({ error, source: 'ListingDoubtsLandlord-Chat' });
    }
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    if (
      isUserLogged &&
      queryParams.get('action') === ACTION_OPEN_DOUBTS_MODAL
    ) {
      openListingDoubtsModal();
      queryParams.delete('action');
      window.history.replaceState(
        {},
        '',
        `${window.location.pathname}?${queryParams.toString()}`
      );
    }
  }, [isUserLogged]);

  const withUserLogged = (cb, actionParam) => () => {
    if (!isUserLogged) {
      const redirectUrl = window.location.origin + window.location.pathname;
      const queryParams = new URLSearchParams(window.location.search);
      queryParams.set('action', actionParam);
      Mediator.publish(EXTERNAL_REDIRECTION_LOGIN_MODAL, {
        initialAction: 'signup',
        renderTitle: action => trans(`${action}.modal.title`),
        renderSubtitle: action => trans(`${action}.modal.subtitle`),
        url: `${redirectUrl}?${queryParams.toString()}`
      });

      SoyuzAnalytics.sendGA4Event('open', {
        section: `${actionParam}-modal-sign-up`
      });
      return;
    }

    cb();
  };

  const handleOnSubmitDoubtsClick = withUserLogged(
    openListingDoubtsModal,
    ACTION_OPEN_DOUBTS_MODAL
  );

  const handleOnStartChatClick = withUserLogged(
    openNewMessageChannel,
    ACTION_CREATE_CHAT
  );

  return (
    <>
      <ListingDoubtsModal
        handleClose={handleModalClose}
        isOpen={isModalOpen}
        listingId={listingId}
        cityId={cityId}
        countryCode={countryCode}
      />
      {!LANDLORD_OUT_OF_MESSAGE_EXPERIMENT.includes(landlordId) &&
      chatExperiment.get('behaviour') === 'variant' ? (
        <div className="listing-doubts">
          <div className="listing-doubts__content">
            <div className="listing-doubts__title">
              {trans('listing.doubts.landlord.title')}
            </div>
            <div className="listing-doubts__text">
              {trans('listing.doubts.landlord.text')}
            </div>
            <Button
              className="listing-doubts__cta"
              color="interaction"
              onClick={handleOnStartChatClick}
            >
              {trans('listing.doubts.landlord.cta')}
            </Button>
          </div>
          <div className="listing-doubts__icon">
            <KeysIcon />
          </div>
        </div>
      ) : (
        <div className="listing-doubts">
          <div className="listing-doubts__info-wrapper">
            <div className="listing-doubts__smile-icon">
              <SmileIcon />
            </div>
            <div>
              <div className="listing-doubts__title">
                {trans('listing.doubts.title')}
              </div>
              <div>{trans('listing.doubts.text')}</div>
            </div>
          </div>
          <div>
            <Button
              className="listing-doubts__cta"
              color="interaction"
              onClick={handleOnSubmitDoubtsClick}
            >
              {trans('listing.doubts.cta')}
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

ListingDoubts.propTypes = {
  listingId: PropTypes.string.isRequired,
  landlordId: PropTypes.string.isRequired,
  cityId: PropTypes.string.isRequired,
  countryCode: PropTypes.string.isRequired
};

export default ListingDoubts;
